import type { ComponentProps } from 'react'

import { useEffect, useRef } from 'react'
import { tv } from 'tailwind-variants'

/**
 * Custom reusable Toolbar component.
 *
 * ArkUI does not provide a toolbar component, but toolbars need to follow
 * WAI-ARIA recommendations about role, focus and roving tabindex.
 *
 * The combination of the useEffect and keyhandler gives the optimal experience:
 * * Tab focuses first toolbar item
 * * Arrow keys cycle toolbar items
 * * Tab again exits the toolbar (contextual based on callback)
 */
export function Toolbar({
  children,
  className,
  'aria-label': ariaLabel,
  onKeyEscape,
  onKeyTab,
  ...props
}: ComponentProps<'div'> & {
  'aria-label': string
  onKeyEscape?: () => void
  onKeyTab?: () => void
}) {
  const toolbarRef = useRef<HTMLDivElement>(null)

  const getToolbarItems = (): HTMLElement[] => {
    if (!toolbarRef.current) return []
    return Array.from(
      toolbarRef.current.querySelectorAll(
        'button:not([disabled]), [role="button"]:not([disabled])',
      ),
    ) as HTMLElement[]
  }

  // Init: Prevent tab from cycling through items, only allow arrow keys
  useEffect(() => {
    const items = getToolbarItems()

    items.forEach((item, index) => {
      item.setAttribute('tabindex', index === 0 ? '0' : '-1')
    })
  }, [children])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!toolbarRef.current) return

    // Handle escape separately
    if (e.key === 'Escape') {
      e.preventDefault()
      if (e.target instanceof HTMLDivElement) return
      // Trigger callback
      onKeyEscape?.()
      return
    }

    // Find all focusable, non-disabled items inside the toolbar
    const items = getToolbarItems()

    const currentActive = document.activeElement as HTMLElement | null
    const currentIndex = currentActive ? items.indexOf(currentActive) : -1
    if (currentIndex === -1) return

    if (e.key === 'Tab') {
      e.preventDefault()

      if (onKeyTab) {
        // Return focus to the Tiptap editor or textarea
        onKeyTab?.()

        return
      } else {
        // Return focus to parent cell
        if (!(e.target instanceof HTMLElement)) return
        const cellElm = e.target.closest('[role="cell"]')

        if (!(cellElm instanceof HTMLDivElement)) return
        cellElm.focus({ preventScroll: true })

        return
      }
    }

    let nextIndex = currentIndex

    // Strict WAI-ARIA Horizontal Navigation Engine Rules
    if (e.key === 'ArrowRight') {
      e.preventDefault()
      nextIndex = (currentIndex + 1) % items.length
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault()
      nextIndex = (currentIndex - 1 + items.length) % items.length
    } else if (e.key === 'Home') {
      e.preventDefault()
      nextIndex = 0
    } else if (e.key === 'End') {
      e.preventDefault()
      nextIndex = items.length - 1
    }

    // Move focus and orchestrate 'roving tabindex'
    const targetItem = items[nextIndex]
    if (!targetItem || nextIndex === currentIndex) return

    // Mutate ONLY the item we left and the item we enter to prevent repaints
    const previousItem = items[currentIndex]
    if (previousItem) previousItem.setAttribute('tabindex', '-1')

    targetItem.setAttribute('tabindex', '0')
    targetItem.focus()
  }

  return (
    <div
      ref={toolbarRef}
      role="toolbar"
      aria-label={ariaLabel}
      aria-orientation="horizontal" // a11y: explicit orientation
      onKeyDown={handleKeyDown}
      className={className}
      {...props}
    >
      {children}
    </div>
  )
}

const separatorStyles = tv({
  base: 'bg-white/10 shrink-0 select-none',
  variants: {
    orientation: {
      vertical: 'w-px h-4 mx-1 self-center',
      horizontal: 'h-px w-full my-1.5',
    },
  },
  defaultVariants: {
    orientation: 'vertical',
  },
})

export function ToolbarSeparator({
  orientation = 'vertical',
  className,
  ...props
}: ComponentProps<'div'> & {
  orientation?: 'vertical' | 'horizontal'
}) {
  return (
    <div
      role="separator" // a11y: compliant role
      aria-orientation={orientation}
      className={separatorStyles({ orientation, className })}
      {...props}
    />
  )
}
